"use client"

import * as React from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"

import { Button } from "@/components/ui/button"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import * as m from "@/paraglide/messages"
import { ApiError } from "@/lib/api/client"
import { useAuth } from "@/lib/auth/auth-context"
import { userStepSchema, type UserStepValues } from "@/lib/signup/schemas"

import { FormError } from "./form-error"

interface UserStepProps {
  defaultValues?: Partial<UserStepValues>
  onSuccess: (values: UserStepValues) => void
  invitationToken?: string
  lockEmail?: boolean
}

export function UserStep({
  defaultValues,
  onSuccess,
  invitationToken,
  lockEmail = false,
}: UserStepProps) {
  const { signup } = useAuth()
  const [serverErrors, setServerErrors] = React.useState<string[]>([])

  const form = useForm<UserStepValues>({
    resolver: zodResolver(userStepSchema),
    defaultValues: {
      name: "",
      lastName: "",
      email: "",
      password: "",
      passwordConfirmation: "",
      phone: "",
      ...defaultValues,
    },
  })

  const onSubmit = form.handleSubmit(async (values) => {
    setServerErrors([])
    try {
      await signup({
        name: values.name,
        lastName: values.lastName,
        email: values.email,
        password: values.password,
        passwordConfirmation: values.passwordConfirmation,
        phone: values.phone || undefined,
        invitationToken,
      })
      onSuccess(values)
    } catch (error) {
      if (error instanceof ApiError) {
        setServerErrors(error.errors)
      } else {
        setServerErrors([m.signup_error_user_generic()])
      }
    }
  })

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="grid gap-4" noValidate>
        <FormError errors={serverErrors} />

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{m.signup_user_name_label()}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={m.signup_user_name_placeholder()}
                    autoComplete="given-name"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{m.signup_user_lastName_label()}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={m.signup_user_lastName_placeholder()}
                    autoComplete="family-name"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="email"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{m.signup_user_email_label()}</FormLabel>
              <FormControl>
                <Input
                  type="email"
                  placeholder={m.signup_user_email_placeholder()}
                  autoComplete="email"
                  readOnly={lockEmail}
                  aria-readonly={lockEmail || undefined}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phone"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{m.signup_user_phone_label()}</FormLabel>
              <FormControl>
                <Input
                  type="tel"
                  placeholder={m.signup_user_phone_placeholder()}
                  autoComplete="tel"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{m.signup_user_password_label()}</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    autoComplete="new-password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="passwordConfirmation"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{m.signup_user_password_confirm_label()}</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    autoComplete="new-password"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Button
          type="submit"
          size="lg"
          className="mt-2 w-full"
          disabled={form.formState.isSubmitting}
        >
          {form.formState.isSubmitting
            ? m.signup_button_creating_account()
            : m.signup_button_continue()}
        </Button>
      </form>
    </Form>
  )
}
